import { type IObservableArray, runInAction, when } from 'mobx';

import type {
  Campaign,
  Targetable,
  Targetables as TargetablesCollection,
  Targeting,
  Targetings as TargetingsCollection,
  TPredicateMode,
} from '@feathr/blackbox';
import type { ListResponse } from '@feathr/rachis';

import { determineInterestsMatchMode } from './Interests/Interests.utils';

export const GEO_TARGETING_KINDS = ['geo', 'geo_set'];

export function getGeoFilters(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Targetable[] {
  return targetings
    .filter(
      (t) =>
        GEO_TARGETING_KINDS.includes(t.get('kind')) &&
        !!t.get('target_data') &&
        !t.get('is_archived'),
    )
    .map((t) => Targetables.get(t.get('target_data')!))
    .filter((f) => f.isEphemeral);
}

/**
 * Gets geo targetables from a list of targetings.
 * Filters for geo targetings that have target_data and are not archived,
 * then retrieves the corresponding targetables.
 *
 * @param targetings - The observable array of targetings to filter
 * @param Targetables - The Targetables collection to fetch targetables from
 * @returns An array of geo targetables, or an empty array if none found
 */
export function getEphemeralGeoTargetables(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Targetable[] {
  const geoTargetings = targetings.filter(
    (t) =>
      t.get('kind') === 'geo' &&
      t.isEphemeral &&
      !!t.get('target_data') &&
      t.get('is_archived') !== true,
  );

  if (geoTargetings.length === 0) {
    return [];
  }

  return getTargetablesFromTargetings(geoTargetings, Targetables).targetables;
}

/**
 * Filters targetings to get only age demographic targetings.
 * Returns targetings with kind 'age_demographic' that have target_data and are not archived.
 *
 * @param targetings - The observable array of targetings to filter
 * @returns An array of age demographic targetings
 */
export function getAgeDemographicTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) =>
      t.get('kind') === 'age_demographic' &&
      !!t.get('target_data') &&
      t.get('is_archived') !== true,
  );
}

/**
 * Filters targetings to get only interest targetings.
 * Returns targetings with kind 'interest' that have target_data and are not archived.
 *
 * @param targetings - The observable array of targetings to filter
 * @returns An array of interest targetings
 */
export function getInterestsTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) => t.get('kind') === 'interest' && !!t.get('target_data') && t.get('is_archived') !== true,
  );
}

/**
 * Filters targetings to get only geo targetings.
 * Returns targetings with kind 'geo' that have target_data and are not archived.
 *
 * @param targetings - The observable array of targetings to filter
 * @returns An array of geo targetings
 */
export function getGeoTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) =>
      GEO_TARGETING_KINDS.includes(t.get('kind')) &&
      !!t.get('target_data') &&
      !t.get('is_archived'),
  );
}

/**
 * Filters out archived targetings.
 * Returns all targetings that are not archived (is_archived !== true).
 *
 * @param targetings - The observable array of targetings to filter
 * @returns An array of non-archived targetings
 */
export function getNonArchivedTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter((t) => t.get('is_archived') !== true);
}

/**
 * Finds the omni awareness targeting from a list of targetings.
 * Returns the first targeting with kind 'omni_awareness' that is not archived.
 *
 * @param targetings - The observable array of targetings to search
 * @returns The omni awareness targeting, or undefined if not found
 */
export function getOmniAwarenessTargeting(
  targetings: IObservableArray<Targeting>,
): Targeting | undefined {
  return targetings.find(
    (targeting) =>
      targeting.get('kind') === 'omni_awareness' && targeting.get('is_archived') !== true,
  );
}

/**
 * Finds the omni lookalike targeting from a list of targetings.
 * Returns the first targeting with kind 'omni_lookalike' that is not archived.
 *
 * @param targetings - The observable array of targetings to search
 * @returns The omni lookalike targeting, or undefined if not found
 */
export function getOmniLookalikeTargeting(
  targetings: IObservableArray<Targeting>,
): Targeting | undefined {
  return targetings.find(
    (targeting) =>
      targeting.get('kind') === 'omni_lookalike' && targeting.get('is_archived') !== true,
  );
}

interface IGetTargetingProps {
  targetings: IObservableArray<Targeting>;
  kinds: string[];
  isArchived?: boolean;
  campaign: Campaign;
}

/**
 * Finds a targeting by its kind that belongs to a specific campaign.
 * Searches for targetings matching any of the provided kinds, belonging to the campaign,
 * with target_data set, and not archived.
 *
 * @param props - The function parameters
 * @param props.kinds - Array of targeting kinds to match against
 * @param props.targetings - The observable array of targetings to search
 * @param props.campaign - The campaign to match the targeting's parent against
 * @returns The matching targeting, or undefined if not found
 */
export function getTargetingByKind({
  kinds,
  targetings,
  campaign,
}: IGetTargetingProps): Targeting | undefined {
  return targetings.find(
    (targeting) =>
      kinds.includes(targeting.get('kind')) &&
      targeting.get('parent') === campaign.get('id') &&
      // Include is_archived === undefined for ephemeral targetings
      targeting.get('is_archived') !== true &&
      !!targeting.get('target_data'),
  );
}

/**
 * Waits for all targetables in the provided array to finish loading.
 * Uses MobX's `when` to wait until each targetable's `isPending` flag becomes false.
 *
 * @param targetablesToWaitFor - Array of targetables to wait for
 * @returns A promise that resolves when all targetables have finished loading
 */
export async function waitForTargetablesToLoad(targetablesToWaitFor: Targetable[]): Promise<void> {
  if (targetablesToWaitFor.length > 0) {
    await Promise.all(targetablesToWaitFor.map((targetable) => when(() => !targetable.isPending)));
  }
}

/**
 * Gets a list response of targetables from a list of targetings using a batched fetch.
 * Extracts all target_data IDs from the targetings and fetches the corresponding targetables
 * in a single API call using the `id__in` filter.
 * Filters out segment targetings since their target_data points to Segments, not Targetables.
 *
 * @param targetingList - Array of targetings to extract target_data IDs from
 * @param Targetables - The Targetables collection to fetch from
 * @returns A ListResponse containing the fetched targetables (may be pending)
 */
export function getTargetablesListFromTargetings(
  targetingList: Targeting[],
  Targetables: TargetablesCollection,
): ListResponse<Targetable> {
  const targetDataIds = targetingList
    .filter((t) => t.get('kind') !== 'segment')
    .map((t) => t.get('target_data'))
    .filter((id): id is string => typeof id === 'string');

  if (targetDataIds.length === 0) {
    return Targetables.list({ filters: { id__in: [] } });
  }

  return Targetables.list({
    filters: { id__in: targetDataIds },
  });
}

/**
 * Gets targetables from a list of targetings, including ephemeral/newly saved ones.
 * First attempts to fetch targetables via batched API call, then checks the local store
 * for any targetables that weren't in the fetched list (e.g., ephemeral or newly saved).
 * Only includes targetables that are fully loaded (not pending).
 *
 * @param targetingList - Array of targetings to get targetables for
 * @param Targetables - The Targetables collection to fetch from and check local store
 * @returns An object containing the array of fully loaded targetables and the loading state
 */
export function getTargetablesFromTargetings(
  targetingList: Targeting[],
  Targetables: TargetablesCollection,
): { targetables: Targetable[]; isPending: boolean } {
  const targetablesListResponse = getTargetablesListFromTargetings(targetingList, Targetables);
  const fetchedTargetables = targetablesListResponse.models;
  const isPending = targetablesListResponse.isPending;

  // For each targeting, get the targetable from fetched list or local store
  const targetables = targetingList
    .map((targeting) => {
      const targetDataId = targeting.get('target_data');
      if (!targetDataId) {
        return undefined;
      }

      // First check fetched list
      let matchingTargetable = fetchedTargetables.find(
        (targetable) => targetable.id === targetDataId,
      );

      // If not found in fetched list, check local store (for ephemeral/newly saved)
      if (!matchingTargetable) {
        const localTargetable = Targetables.get(targetDataId);
        // Only use if fully loaded and has required fields
        if (localTargetable && !localTargetable.isPending) {
          matchingTargetable = localTargetable;
        }
      }

      return matchingTargetable;
    })
    .filter((targetable): targetable is Targetable => targetable !== undefined);

  return { targetables, isPending };
}

/**
 * Extracts target_data IDs from an array of targetings.
 * Filters out any undefined or non-string values.
 *
 * @param targetings - Array of targetings to extract IDs from
 * @returns Array of target_data ID strings
 */
function getTargetDataIdsFromTargetings(targetings: Targeting[]): string[] {
  return targetings
    .map((t) => t.get('target_data'))
    .filter((t): t is string => typeof t === 'string');
}

/**
 * Extracts geo filter IDs from an array of targetables.
 * Maps each targetable to its geo_filter ID and filters out undefined values.
 *
 * @param targetables - Array of targetables to extract geo filter IDs from
 * @returns Array of geo filter ID strings
 */
function extractGeoFilterIds(targetables: Targetable[]): string[] {
  return targetables
    .map((targetable) => targetable.get('geo_filter'))
    .filter((id): id is string => typeof id === 'string');
}

/**
 * Extracts interest filter IDs from targetables by matching targetable IDs.
 * For each ID in the provided array, finds the corresponding targetable and extracts
 * its interest ID from the ITradedeskTargetInterest object.
 *
 * @param ids - Array of targetable IDs to look up
 * @param targetables - Array of targetables to search through
 * @returns Array of interest ID strings from the matched targetables
 */
function extractInterestFilterIds(ids: string[], targetables: Targetable[]): string[] {
  return ids
    .map((id) => {
      const targetable = targetables.find((t) => t.id === id);

      if (!targetable) {
        return undefined;
      }

      return targetable.get('interest').id;
    })
    .filter((id): id is string => id !== undefined);
}

/**
 * Extracts age category filter IDs from an array of targetables.
 * Maps each targetable to its age_category value and filters out non-string values.
 *
 * @param targetables - Array of targetables to extract age category IDs from
 * @returns Array of age category ID strings
 */
function extractAgeCategoryFilterIds(targetables: Targetable[]): string[] {
  return targetables
    .map((targetable) => targetable.get('age_category'))
    .filter((ageCategory): ageCategory is string => typeof ageCategory === 'string');
}

/**
 * Converts geo targetings to a simplified array format.
 * Extracts target_data and included status from geo targetings into a plain object array.
 *
 * @param targetings - The observable array of targetings to process
 * @returns Array of objects with target_data and included properties
 */
export function getGeoTargetingsArray(
  targetings: IObservableArray<Targeting>,
): Array<{ target_data: string; included: boolean }> {
  return getGeoTargetings(targetings)
    .map((t) => ({
      target_data: t.get('target_data') as string,
      included: t.get('included') === true,
    }))
    .filter((t): t is { target_data: string; included: boolean } => !!t.target_data);
}

/**
 * Gets interest IDs and match mode from interest targetings.
 * Extracts interest targetings, gets their corresponding targetables, and determines
 * the match mode. Note: targetables must be fully loaded before calling this function.
 *
 * @param targetings - The observable array of targetings to process
 * @param targetables - Array of targetables (must be fully loaded)
 * @returns Object containing interestIds array and matchMode
 */
export function getInterestIds(
  targetings: IObservableArray<Targeting>,
  targetables: Targetable[],
): { interestIds: string[]; matchMode: TPredicateMode } {
  // Get just the interest targetings
  const interestTargetings = getInterestsTargetings(targetings);
  // Get the targetable ids from the interest targetings
  const interestTargetDataIds = getTargetDataIdsFromTargetings(interestTargetings);
  // Use the targetables to get the interest ids
  const targetableInterestIds = extractInterestFilterIds(interestTargetDataIds, targetables);

  return {
    interestIds: targetableInterestIds,
    matchMode: determineInterestsMatchMode(interestTargetings),
  };
}

/**
 * Gets included and excluded geo filter IDs from geo targetings.
 * For geo_set targetings, extracts included_geo_filters and excluded_geo_filters from the targetable.
 * For regular geo targetings, separates by included/excluded status, fetches their targetables,
 * waits for them to load, then extracts the geo filter IDs.
 *
 * @param targetings - The observable array of targetings to process
 * @param Targetables - The Targetables collection to fetch targetables from
 * @returns Promise resolving to an object with included/excluded geo filter ID arrays and optional geoset name
 */
export async function getGeoFilterIds(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Promise<{ included: string[]; excluded: string[]; geosetName?: string }> {
  const geoTargetings = getGeoTargetings(targetings);

  if (geoTargetings.length === 0) {
    return { included: [], excluded: [] };
  }

  const geoSetTargeting = geoTargetings.find((t) => t.get('kind') === 'geo_set');

  if (geoSetTargeting) {
    const targetData = geoSetTargeting.get('target_data');
    if (!targetData) {
      return { included: [], excluded: [] };
    }

    const geoSetTargetableList = Targetables.list({
      filters: { id__in: [targetData] },
    });
    await when(() => !geoSetTargetableList.isPending);

    const geoSetTargetable = geoSetTargetableList.models[0];
    if (!geoSetTargetable) {
      return { included: [], excluded: [] };
    }

    return {
      included: geoSetTargetable.get('included_geo_filters') ?? [],
      excluded: geoSetTargetable.get('excluded_geo_filters') ?? [],
      geosetName: geoSetTargetable.get('name'),
    };
  }

  const includedGeoTargetings = geoTargetings.filter((t) => t.get('included') === true);
  const excludedGeoTargetings = geoTargetings.filter((t) => t.get('included') === false);

  const { targetables: includedGeoTargetables } = getTargetablesFromTargetings(
    includedGeoTargetings,
    Targetables,
  );
  const { targetables: excludedGeoTargetables } = getTargetablesFromTargetings(
    excludedGeoTargetings,
    Targetables,
  );

  await waitForTargetablesToLoad([...includedGeoTargetables, ...excludedGeoTargetables]);

  return {
    included: extractGeoFilterIds(includedGeoTargetables),
    excluded: extractGeoFilterIds(excludedGeoTargetables),
  };
}

/**
 * Gets age category IDs from age demographic targetings.
 * Fetches targetables for age demographic targetings, waits for them to load,
 * then extracts the age category IDs.
 *
 * @param targetings - The observable array of targetings to process
 * @param Targetables - The Targetables collection to fetch targetables from
 * @returns Promise resolving to an array of age category ID strings
 */
export async function getAgeCategoryIds(
  targetings: IObservableArray<Targeting>,
  Targetables: TargetablesCollection,
): Promise<string[]> {
  const ageCategoryTargetings = getAgeDemographicTargetings(targetings);
  const { targetables: ageCategoryTargetables } = getTargetablesFromTargetings(
    ageCategoryTargetings,
    Targetables,
  );

  await waitForTargetablesToLoad(ageCategoryTargetables);

  return extractAgeCategoryFilterIds(ageCategoryTargetables);
}

interface ICreateTargeting {
  campaign: Campaign;
  Targetings: TargetingsCollection;
  targetableId: string;
  kind: string;
  targetings: IObservableArray<Targeting>;
}

/**
 * Creates a new targeting and adds it to the targetings array.
 * Creates a targeting with the specified kind, sets it as included by default,
 * associates it with the provided targetable, and marks it as dirty.
 * The targeting is automatically added to the targetings array.
 *
 * @param props - The function parameters
 * @param props.targetableId - The ID of the targetable to associate with the targeting
 * @param props.Targetings - The Targetings collection to create the targeting in
 * @param props.kind - The kind of targeting to create
 * @param props.campaign - The campaign to set as the targeting's parent
 * @param props.targetings - The observable array to add the new targeting to
 * @returns The newly created targeting
 */
export function createTargeting({
  targetableId,
  Targetings,
  kind,
  campaign,
  targetings,
}: ICreateTargeting): Targeting {
  const model = Targetings.create({
    parent: campaign.get('id'),
    kind,
    included: true,
  });

  runInAction(() => {
    // Make it dirty by setting after creation
    model.set({
      target_data: targetableId,
    });

    // Add it to the targetings array
    targetings.push(model);
  });

  return model;
}

interface IRemoveTargetingsOptions {
  targetingsToRemove: Targeting[];
  targetingsArray: IObservableArray<Targeting>;
  Targetings: TargetingsCollection;
  additionalFieldsOnArchive?: Record<string, unknown>;
}

/**
 * Remove or archive a list of targetings.
 * Ephemeral targetings are removed from the array and collection.
 * Persisted targetings are archived by setting is_archived: true.
 * @param targetingsToRemove - The targetings to remove or archive
 * @param targetingsArray - The observable array of targetings
 * @param Targetings - The targetings collection
 * @param additionalFieldsOnArchive - Additional fields to set when archiving (e.g. is_enabled: false)
 */
export function removeOrArchiveTargetings({
  targetingsToRemove,
  targetingsArray,
  Targetings,
  additionalFieldsOnArchive = {},
}: IRemoveTargetingsOptions): void {
  runInAction(() => {
    targetingsToRemove.forEach((targeting) => {
      if (targeting.isEphemeral) {
        targetingsArray.remove(targeting);
        Targetings.remove(targeting.id);
      } else {
        targeting.set({ is_archived: true, ...additionalFieldsOnArchive });
      }
    });
  });
}

/**
 * Remove or archive a single targeting.
 * Convenience wrapper around removeOrArchiveTargetings for single targeting.
 * @param targeting - The targeting to remove or archive
 * @param targetingsArray - The observable array of targetings
 * @param Targetings - The targetings collection
 * @param additionalFieldsOnArchive - Additional fields to set when archiving (e.g. is_enabled: false)
 */
export function removeOrArchiveSingleTargeting(
  targeting: Targeting,
  targetingsArray: IObservableArray<Targeting>,
  Targetings: TargetingsCollection,
  additionalFieldsOnArchive: Record<string, unknown> = {},
): void {
  removeOrArchiveTargetings({
    targetingsToRemove: [targeting],
    targetingsArray,
    Targetings,
    additionalFieldsOnArchive,
  });
}

/**
 * Extract geo filter IDs from a list of targetings (synchronous version).
 * Unlike getGeoFilterIds, this doesn't wait for targetables to load.
 * Use this when you know targetables are already loaded (e.g. in modal with ephemeral targetings).
 * @param targetingsList - The targetings to extract geo filter IDs from
 * @param Targetables - The targetables collection
 * @returns Object with included and excluded geo filter ID arrays
 */
export function extractGeoFilterIdsFromTargetings(
  targetingsList: Targeting[],
  Targetables: TargetablesCollection,
): { included: string[]; excluded: string[] } {
  const included: string[] = [];
  const excluded: string[] = [];

  for (const targeting of targetingsList) {
    const targetableId = targeting.get('target_data');
    if (!targetableId) {
      continue;
    }

    const targetable = Targetables.get(targetableId);
    const geoFilterId = targetable?.get('geo_filter');
    if (!geoFilterId) {
      continue;
    }

    if (targeting.get('included')) {
      included.push(geoFilterId);
    } else {
      excluded.push(geoFilterId);
    }
  }

  return { included, excluded };
}
